import { gql, useMutation, useQuery } from '@apollo/client'
import { useState } from 'react'

import {
  PaymentDetailFragmentDoc,
  PersonalInfoFragmentDoc,
  PersonalInfoQuery,
  PersonalInfoQueryVariables,
  SendVerificationSms,
  SendVerificationSmsMutation,
  SendVerificationSmsMutationVariables,
  UpdateUserDeliveryInfo,
  UpdateUserDeliveryInfoMutation,
  UpdateUserDeliveryInfoMutationVariables,
  VerifySmsCode,
  VerifySmsCodeMutation,
  VerifySmsCodeMutationVariables,
} from '@/clients/utoniq-core/schema'
import { useMessage, useTranslation } from '@/core'
import { makeViewModel } from '@/core/architecture'

import { Action, State } from './template'

export const useViewModel = makeViewModel<State, Action>(() => {
  const { t } = useTranslation('personalInfoSettings')
  const { showMessage } = useMessage()
  const [needVerifyPhoneNumber, setNeedVerifyPhoneNumber] =
    useState<boolean>(false)

  const { data, refetch } = useQuery<
    PersonalInfoQuery,
    PersonalInfoQueryVariables
  >(QUERY)

  const [updateUserDeliveryInfo, { loading: submitting }] = useMutation<
    UpdateUserDeliveryInfoMutation,
    UpdateUserDeliveryInfoMutationVariables
  >(UpdateUserDeliveryInfo)

  const [verifySMSCode, { loading: verifyingSMSCode }] = useMutation<
    VerifySmsCodeMutation,
    VerifySmsCodeMutationVariables
  >(VerifySmsCode)

  const [sendVerificationSMS, { loading: sendingSMSCode }] = useMutation<
    SendVerificationSmsMutation,
    SendVerificationSmsMutationVariables
  >(SendVerificationSms)

  return {
    state: {
      user: data?.user,
      submitting,
      sendingSMSCode,
      paymentSource: data?.paymentSource ?? null,
      isMembershipPersonalInformationRequired:
        !!data?.user?.isMembershipPersonalInformationRequired,
      verifyingSMSCode,
      needVerifyPhoneNumber,
    },
    actions: {
      async onSubmit(values) {
        return updateUserDeliveryInfo({
          variables: {
            updateUserDeliveryInfoInputs: {
              fullName: values.fullName,
              phoneNumber: values.phoneNumber,
              birthday: values.birthday,
              gender: values.gender,
              address: {
                postalCode: values.postalCode,
                prefecture: values.prefecture,
                city: values.city,
                line1: values.line1,
                line2: values.line2,
              },
            },
          },
        }).then(({ data }) => {
          if (!data?.updateUserDeliveryInfo.isPhoneNumberVerified) {
            return setNeedVerifyPhoneNumber(true)
          }
          showMessage(t('registerSuccess'), 'success')
        })
      },
      onChangeNeedVerifyPhoneNumber(needVerifyPhoneNumber) {
        setNeedVerifyPhoneNumber(needVerifyPhoneNumber)
      },
      async onVerificationPhoneNumber({ code }) {
        await verifySMSCode({ variables: { code } })
        await refetch()
        showMessage(t('registerSuccess'), 'success')
      },
      async onSendVerificationSMS() {
        await sendVerificationSMS()
      },
      async onResendVerificationSMS() {
        await sendVerificationSMS()
      },
    },
  }
})

const QUERY = gql`
  query personalInfo {
    user {
      ...PersonalInfo
    }
    paymentSource {
      ...PaymentDetail
    }
  }
  ${PersonalInfoFragmentDoc}
  ${PaymentDetailFragmentDoc}
`

const UPDATE_PERSONAL_INFORMATION_SETTINGS = gql`
  mutation updatePersonalInformationSettings($inputs: UpdateProfileInput!) {
    updateUserProfile(inputs: $inputs) {
      ...PersonalInfo
    }
  }
  ${PersonalInfoFragmentDoc}
`
